import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function ChatInterface({ mode, config, language, setLanguage, user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [training, setTraining] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);


  // Load chat history when user or mode changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!user?.email) {
        console.log('[History] No user email, skipping');
        return;
      }

      console.log('[History] Fetching for:', user.email, 'mode:', mode);

      try {
        setLoading(true);
        const response = await axios.get(`/api/chat/history?email=${encodeURIComponent(user.email)}`);
        const historyData = response.data;

        console.log('[History] Received data, is array:', Array.isArray(historyData), 'length:', historyData?.length);

        if (Array.isArray(historyData)) {
          const parsedMessages = historyData.map(item => {
            let content = '';
            
            if (item.from === 'human') {
              try {
                // Try to parse payload as JSON (format: "{\"message\":\"hi\",...}")
                const payloadObj = JSON.parse(item.payload);
                content = payloadObj.message || item.payload;
              } catch (e) {
                // If parse fails, use raw payload
                content = item.payload;
              }
            } else {
              // AI messages seem to be plain text strings
              content = item.payload;
            }

            const timestamp = new Date(item.createdAt);

            return {
              type: item.from === 'human' ? 'user' : 'bot',
              content: content,
              timestamp: timestamp
            };
          });

          // Sort by timestamp
          parsedMessages.sort((a, b) => a.timestamp - b.timestamp);
          
          console.log('[History] Parsed', parsedMessages.length, 'messages, calling setMessages');
          setMessages(parsedMessages);
          console.log('[History] setMessages called');
          
          // Scroll to bottom instantly after loading history
          setTimeout(() => scrollToBottom('auto'), 0);
          
          // If there are messages, try to restore sessionId from the last user message
          // This is a best-effort to resume the last session
          const lastUserMsg = [...historyData].reverse().find(m => m.from === 'human');
          if (lastUserMsg) {
             try {
                const payloadObj = JSON.parse(lastUserMsg.payload);
                if (payloadObj.sessionId) {
                  setSessionId(payloadObj.sessionId);
                }
             } catch (e) {
               // ignore
             }
          }
        } else {
          console.log('[History] Response is not an array:', historyData);
        }
      } catch (error) {
        console.error('[History] Failed to load chat history:', error);
      } finally {
        console.log('[History] Setting loading to false');
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user, mode]);

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!input.trim() || loading) return;

    // Generate sessionId on first message
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = generateSessionId();
      setSessionId(currentSessionId);
    }

    const userMessage = {
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const endpoint = mode === 'basic' ? '/api/chat/basic' : '/api/chat/knowledge';
      const response = await axios.post(endpoint, {
        message: input,
        sessionId: currentSessionId,
        language: language,
        email: user?.email,
        training
      });

      // Extract the reply text from various possible response formats
      let replyContent = '';
      if (typeof response.data === 'string') {
        replyContent = response.data;
      } else if (response.data.output) {
        replyContent = response.data.output;
      } else if (response.data.reply) {
        replyContent = response.data.reply;
      } else if (response.data.message) {
        replyContent = response.data.message;
      } else {
        replyContent = JSON.stringify(response.data);
      }

      const botMessage = {
        type: 'bot',
        content: replyContent,
        timestamp: new Date(response.data.timestamp || Date.now())
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorDetails = error.response?.data?.error || error.message || 'Unknown error';
      const errorMessage = {
        type: 'error',
        content: `Sorry, I encountered an error: ${errorDetails}. Please try again.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      // Refocus input after response
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const clearChatHistory = async () => {
    if (!user?.email) {
      console.error('[Clear Chat] No user email, skipping');
      return;
    }

    try {
      setLoading(true);
      setClearing(true);
      console.log('[Clear Chat] Sending clear history request for:', user.email);
      // Use backend proxy instead of direct webhook
      const response = await axios.delete(`/api/chat/clear-history?email=${encodeURIComponent(user.email)}`);

      if (response.status === 200) {
        console.log('[Clear Chat] History cleared successfully');
        setMessages([]);
        setSessionId(null);
      } else {
        console.error('[Clear Chat] Failed to clear history, status:', response.status);
      }
    } catch (error) {
      console.error('[Clear Chat] Error clearing history:', error);
    } finally {
      setLoading(false);
      setClearing(false);
    }
  };

  console.log('[Render] messages.length:', messages.length, 'loading:', loading, 'user:', user?.email);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header - Hidden on mobile */}
      <header className="hidden lg:flex items-center justify-between px-12 py-8 border-b border-gray-100 bg-white">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-[36px]">
              {mode === 'basic' ? 'chat' : 'auto_stories'}
            </span>
            {config?.chatTitle || 'AI Chat Assistant'}
          </h1>
          <p className="text-gray-500 mt-1">
            {config?.chatDescription || 'Ask me anything! I\'m here to help with your questions.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {config?.trainingMode && (
            <label className="flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 bg-gray-50">
              <span className="text-xs font-semibold text-gray-700">
                {language === 'zh-TW' ? '訓練模式' : 'Training'}
              </span>
              <button
                type="button"
                onClick={() => setTraining(prev => !prev)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  training ? 'bg-primary' : 'bg-gray-300'
                }`}
                aria-pressed={training}
                aria-label={language === 'zh-TW' ? '切換訓練模式' : 'Toggle training mode'}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    training ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          )}
          <div className="inline-flex rounded-full border-2 border-primary overflow-hidden">
            <button
              onClick={() => setLanguage('en')}
              className={`px-6 py-2 text-sm font-semibold transition-all ${
                language === 'en'
                  ? 'bg-primary text-white'
                  : 'bg-white text-primary'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('zh-TW')}
              className={`px-6 py-2 text-sm font-semibold transition-all ${
                language === 'zh-TW'
                  ? 'bg-primary text-white'
                  : 'bg-white text-primary'
              }`}
            >
              繁中
            </button>
          </div>
          <button
            onClick={clearChatHistory}
            disabled={loading || clearing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
            title={language === 'zh-TW' ? '重新開始' : 'Restart Chat'}
          >
            <span className={`material-symbols-outlined text-[20px] ${clearing ? 'animate-spin' : ''}`}>
              {clearing ? 'refresh' : 'chat_add_on'}
            </span>
            {clearing 
              ? (language === 'zh-TW' ? '重置中...' : 'Restarting...') 
              : (language === 'zh-TW' ? '重新開始' : 'New Chat')}
          </button>
        </div>
      </header>
      
      {/* Mobile Top Actions - Toolbar */}
      <div className="lg:hidden flex justify-between items-center px-4 py-2 border-b border-gray-100 bg-white/95 backdrop-blur-sm z-10">
         {config?.trainingMode ? (
          <label className="flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-gray-200 bg-gray-50">
            <span className="text-[11px] font-semibold text-gray-700">
              {language === 'zh-TW' ? '訓練' : 'Training'}
            </span>
            <button
              type="button"
              onClick={() => setTraining(prev => !prev)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                training ? 'bg-primary' : 'bg-gray-300'
              }`}
              aria-pressed={training}
              aria-label={language === 'zh-TW' ? '切換訓練模式' : 'Toggle training mode'}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  training ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </label>
         ) : (
          <div />
         )}
         <button
            onClick={clearChatHistory}
            disabled={loading || clearing}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-red-500 bg-gray-50 hover:bg-red-50 rounded-full transition-all border border-gray-200"
            title={language === 'zh-TW' ? '重新開始' : 'Restart Chat'}
          >
            <span className={`material-symbols-outlined text-[18px] ${clearing ? 'animate-spin' : ''}`}>
              {clearing ? 'refresh' : 'chat_add_on'}
            </span>
            <span>{language === 'zh-TW' ? '新對話' : 'New Chat'}</span>
         </button>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4 lg:px-12 lg:py-10 bg-gray-50/30">
        <div className="max-w-4xl mx-auto space-y-6">
          {config?.trainingMode && (
            <div className="flex justify-center">
              <span
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${
                  training
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-100 text-gray-600 border-gray-200'
                }`}
              >
                <span className="material-symbols-outlined text-[14px]">school</span>
                {language === 'zh-TW'
                  ? `訓練模式：${training ? '開啟' : '關閉'}`
                  : `Training: ${training ? 'ON' : 'OFF'}`}
              </span>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[48px] text-primary">
                  {mode === 'basic' ? 'chat_bubble' : 'psychology'}
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{config?.chatWelcomeTitle || 'Start a Conversation'}</h3>
              <p className="text-gray-500 text-center max-w-md">
                {mode === 'basic' 
                  ? (config?.chatWelcomeSubtitle || 'Type your message below to begin chatting with the AI assistant.')
                  : (config?.chatKnowledgeWelcomeSubtitle || 'Ask questions about your documents and I\'ll provide answers based on your knowledge base.')}
              </p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} ${
                  msg.type === 'system' ? 'justify-center' : ''
                }`}
              >
                {msg.type === 'system' ? (
                  <div className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600 font-medium">
                    {msg.content}
                  </div>
                ) : (
                  <div className={`flex gap-3 max-w-2xl ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      msg.type === 'user' 
                        ? 'bg-bubble-user' 
                        : msg.type === 'error'
                        ? 'bg-red-100 text-red-500'
                        : 'bg-primary/10'
                    }`}>
                      <span className={`material-symbols-outlined text-[20px] ${
                        msg.type === 'user' ? 'text-white' : msg.type === 'error' ? 'text-red-500' : 'text-primary'
                      }`}>
                        {msg.type === 'user' ? 'person' : msg.type === 'error' ? 'error' : 'smart_toy'}
                      </span>
                    </div>
                    <div className={`flex flex-col ${msg.type === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`px-5 py-3 rounded-2xl shadow-sm ${
                        msg.type === 'user'
                          ? 'bg-bubble-user text-white'
                          : msg.type === 'error'
                          ? 'bg-red-50 text-red-800 border border-red-200'
                          : 'bg-white border border-gray-200'
                      }`}>
                        {msg.type === 'user' || msg.type === 'error' ? (
                          <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                        ) : (
                          <div className="text-[15px] leading-relaxed markdown-content">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                      <div className={`flex items-center gap-2 mt-1 px-2 ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                        <span className="text-xs text-gray-400">
                          {(() => {
                            const date = new Date(msg.timestamp);
                            if (isNaN(date.getTime())) return '';
                            
                            const now = new Date();
                            const isToday = date.getDate() === now.getDate() && 
                                           date.getMonth() === now.getMonth() && 
                                           date.getFullYear() === now.getFullYear();
                            
                            if (isToday) {
                              return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            }
                            return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                          })()}
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(msg.content)}
                          className="text-gray-400 hover:text-primary transition-colors flex items-center justify-center bg-transparent border-0 cursor-pointer p-0"
                          title="Copy message"
                        >
                          <span className="material-symbols-outlined text-[14px]">content_copy</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="flex gap-3 max-w-2xl">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-primary/10">
                  <span className="material-symbols-outlined text-[20px] text-primary">smart_toy</span>
                </div>
                <div className="px-5 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Form */}
      <form onSubmit={sendMessage} className="px-4 py-4 lg:px-12 lg:py-6 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                rows={1}
                placeholder="Type your message..."
                disabled={loading}
                className="w-full px-6 py-4 pr-12 bg-gray-50 border border-gray-200 rounded-input text-[15px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed overflow-y-auto"
                style={{ minHeight: '56px', maxHeight: '150px' }}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-5 py-4 bg-primary hover:bg-primary-dark text-white rounded-input font-semibold shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transform active:scale-95"
            >
              <span className="material-symbols-outlined">send</span>
            </button>
          </div>
          <p className="text-[11px] text-center text-gray-400 mt-3 select-none">
            {language === 'zh-TW' 
              ? 'AI 生成的內容可能不準確，請自行查證重要資訊。'
              : 'AI-generated content may be inaccurate. Please verify important information.'}
          </p>
        </div>
      </form>
    </div>
  );
}

export default ChatInterface;
